// The accounting system the ledger export goes to (technical design §8.2): which QuickBooks Online
// or Xero company is connected, connecting one (the provider's consent page, then back here) and
// disconnecting. Coming back from the provider, the hash says how it went (ledger-connect.ts).
// Riverstone's; Blueprint draws AccountingSystem.tsx.

import { useEffect, useState } from 'react';
import type { BooksProps } from './props.ts';
import { dayText } from './model.ts';
import { connectUrl, LABEL, takeLedgerReturn, type Connection, type Target } from './ledger-connect.ts';
import { LedgerExport } from './LedgerExport.tsx';
import { Loading, Panel, useAct, useQuery } from './ui.tsx';
import { useDesign } from '../blueprint/design.ts';

export function Connections({ toast, timeZone }: Pick<BooksProps, 'toast' | 'timeZone'>) {
  const q = useQuery<{ connection: Connection | null }>('exports.ledgerConnection', {});
  const act = useAct(toast, () => q.reload());
  const [busy, setBusy] = useState(false);
  const bp = useDesign() === 'blueprint';
  useEffect(() => {
    const said = takeLedgerReturn();
    if (said) toast(said);
  }, []);
  const connect = (t: Target) => {
    setBusy(true);
    void connectUrl(t)
      .then((url) => location.assign(url))
      .catch((e: unknown) => {
        toast(e instanceof Error ? e.message : 'That did not work. Try again.');
        setBusy(false);
      });
  };
  if (!q.data) return <Loading error={q.error} what="the accounting system" retry={() => void q.reload()} />;
  const c = q.data.connection;
  return (
    <>
      <Panel
        title="Accounting system"
        aside={<span className={`chip say ${c ? 'ok' : ''}`}>{c ? 'Connected' : 'Not connected'}</span>}
      >
        {c ? (
          <p>
            The ledger export goes to <strong>{LABEL[c.target]}</strong>
            {c.companyName ? <> ({c.companyName})</> : null}, connected {dayText(c.connectedAt)}.
            {c.fake ? (
              <span className="muted small"> A stand-in for testing: nothing leaves Flowology.</span>
            ) : null}
          </p>
        ) : (
          <p className="muted">
            Connect the company’s QuickBooks Online or Xero to export the books there. You approve it on their
            site and come back here.
          </p>
        )}
        <div className="row-actions">
          {(['quickbooks', 'xero'] as const).map((t) => (
            <button
              key={t}
              // Connecting is the panel's one primary only while nothing is connected; in Blueprint, one
              // primary in a zone (DESIGN.md §3a): QuickBooks's, Xero's beside it quiet
              className={`btn ${c || (bp && t === 'xero') ? 'quiet' : 'primary'}`}
              disabled={busy || act.busy}
              onClick={() => connect(t)}
            >
              {c?.target === t ? `Connect ${LABEL[t]} again` : `Connect ${LABEL[t]}`}
            </button>
          ))}
          {c && (
            <button
              className="btn quiet"
              disabled={busy || act.busy}
              onClick={() => {
                if (confirm(`Disconnect ${LABEL[c.target]}? What was exported stays there.`))
                  void act.run('exports.disconnectLedger', {}, `${LABEL[c.target]} is disconnected.`);
              }}
            >
              Disconnect
            </button>
          )}
        </div>
      </Panel>
      {c && (
        <LedgerExport key={c.target} target={c.target} toast={toast} {...(timeZone ? { timeZone } : {})} />
      )}
    </>
  );
}
